import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  RefreshCwIcon,
  TriangleAlertIcon,
  FileTextIcon,
} from "../../../components/icons";
import ViewHeader from "./ViewHeader";
import PaneStatus from "../../../components/feedback/PaneStatus";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Spinner } from "../../../components/ui/feedback";
import { Tooltip } from "../../../components/ui/tooltip";
import {
  getExistingReferences,
  selectExistingSDT,
  DocumentReference,
} from "../../../platform/word/documentTools";
import { ReferenceValidationResult } from "../types/reference-validation";
import { useValidateReferencesMutation } from "../api/playbookApi";
import { useAssemblyContext } from "../context/AssemblyContext";

interface ExistingReferencesProps {
  onEditReference: (reference: DocumentReference) => void;
  onBack: () => void;
}

const ExistingReferences: React.FC<ExistingReferencesProps> = ({ onEditReference, onBack }) => {
  const ctx = useAssemblyContext();
  const [references, setReferences] = useState<DocumentReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [validationResults, setValidationResults] = useState<
    Map<string, ReferenceValidationResult>
  >(new Map());

  const [validateReferences] = useValidateReferencesMutation();

  const validateExistingReferences = async (referenceIds: string[]) => {
    try {
      const results = await validateReferences({ ctx, referenceIds }).unwrap();
      const resultMap = new Map<string, ReferenceValidationResult>();
      results.forEach((result) => {
        resultMap.set(result.stableObjectIdentifier, result);
      });
      setValidationResults(resultMap);
    } catch (err) {
      console.error("Failed to validate references:", err);
    }
  };

  const loadReferences = async () => {
    try {
      setError(null);
      const existingRefs = await getExistingReferences();
      setReferences(existingRefs);
      if (existingRefs.length > 0) {
        await validateExistingReferences(existingRefs.map((ref) => ref.stableObjectIdentifier));
      }
    } catch (err) {
      setError("Failed to load existing references");
      console.error("Error loading references:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadReferences();
  };

  const handleEditAndShow = async (reference: DocumentReference) => {
    try {
      const success = await selectExistingSDT(reference.stableObjectIdentifier);
      if (!success) {
        setError("Failed to locate reference in document");
      } else {
        onEditReference(reference);
      }
    } catch (err) {
      setError("Failed to show reference in document");
      console.error("Error showing reference:", err);
    }
  };

  if (loading) {
    return <PaneStatus loading title="Reading the document…" />;
  }

  const count = references.length;

  return (
    <div className="flex flex-col gap-4 px-3.5 pt-3 pb-4">
      <ViewHeader
        title={count > 0 ? `Existing references (${count})` : "Existing references"}
        onBack={onBack}
        action={
          // A disabled button takes no pointer events, so the tooltip hangs off a span.
          <Tooltip title="Refresh">
            <span className="inline-flex">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Refresh"
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-muted-foreground"
              >
                {refreshing ? <Spinner size={14} /> : <RefreshCwIcon />}
              </Button>
            </span>
          </Tooltip>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {count === 0 ? (
        <PaneStatus
          icon={<FileTextIcon className="size-7" />}
          title="No references yet"
          description="References appear here once you insert them into the document."
        />
      ) : (
        <ul className="overflow-hidden rounded-lg border border-border">
          {references.map((reference) => {
            const validation = validationResults.get(reference.stableObjectIdentifier);
            const invalid = validation !== undefined && !validation.isValid;
            return (
              <li
                key={reference.stableObjectIdentifier}
                className="relative not-first:border-t not-first:border-border"
              >
                <button
                  type="button"
                  onClick={() => handleEditAndShow(reference)}
                  className="flex w-full items-center py-2 pr-12 pl-4 text-left transition-colors outline-none hover:bg-accent focus-visible:bg-accent"
                >
                  {/* No tooltip of its own: the same message is the row's second line. */}
                  {invalid && <TriangleAlertIcon className="mr-2 flex-none text-warning" />}
                  <span className="my-1 flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-body1 font-medium text-strong">
                      {reference.label}
                    </span>
                    {invalid && (
                      <span className="text-caption text-warning-dark">{validation.message}</span>
                    )}
                  </span>
                </button>
                {/* Over the row's right padding rather than inside the row button: a
                    button may not contain another. */}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Edit reference"
                  title="Edit reference"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditAndShow(reference);
                  }}
                  className="absolute top-1/2 right-1 -translate-y-1/2 text-muted-foreground"
                >
                  <PencilIcon />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ExistingReferences;
